import React, { useState } from 'react';
import { SLIM_LOGO_PROD } from '../../../../utils/slimLogo';
import { resolveMediaUrl } from '../../Common/DetailParts';
import { Icon } from '../../Common/Icons';

/** 10-digit phone: spaces and symbols removed, +94 / 94 → 0 (same rule as the server). '' if not valid. */
export const normalizePhone = (value) => {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('94')) d = `0${d.slice(2)}`;
  if (d.length === 9 && /^[1-9]/.test(d)) d = `0${d}`;
  return /^0\d{9}$/.test(d) ? d : '';
};

export const fmtMoney = (n, currency = 'LKR') => (Number(n) > 0 ? `${currency} ${Number(n).toLocaleString('en-LK', { minimumFractionDigits: 2 })}` : 'Free');
export const fmtEventDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '');
export const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

/** Page frame for the public ticket pages: SLIM header, content, footer. */
export const PublicShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-900">
    <header className="border-b border-gray-100 bg-white/80 backdrop-blur print:hidden">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <img src={SLIM_LOGO_PROD} alt="Sri Lanka Institute of Marketing" className="h-9 w-auto" />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
          <Icon name="lock" className="h-3.5 w-3.5" />Secure checkout
        </span>
      </div>
    </header>
    <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10">{children}</main>
    <footer className="border-t border-gray-100 py-6 text-center text-xs text-gray-400 print:hidden">
      © {new Date().getFullYear()} Sri Lanka Institute of Marketing · Tickets are personal and checked at the entrance.
    </footer>
  </div>
);

/** True for light colours, where white text would be hard to read. */
const isLight = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
};

const DateTile = ({ date, color, small }) => {
  if (!date) return null;
  const d = new Date(date);
  return (
    <div className={`flex shrink-0 flex-col overflow-hidden rounded-2xl bg-white text-center shadow-lg ring-1 ring-black/5 ${small ? 'w-16' : 'w-20 sm:w-24'}`}>
      <span className="py-1 text-[0.6875rem] font-bold uppercase tracking-[0.2em] text-white" style={{ background: color }}>
        {d.toLocaleDateString('en-GB', { month: 'short' })}
      </span>
      <span className={`font-extrabold leading-none text-gray-900 ${small ? 'pt-1.5 text-2xl' : 'pt-2 text-3xl sm:text-4xl'}`}>{d.getDate()}</span>
      <span className="pb-2 pt-1 text-[0.625rem] font-semibold uppercase tracking-wider text-gray-500">{d.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
    </div>
  );
};

const EventFacts = ({ event, chipCls }) => (
  <div className="mt-3 flex flex-wrap gap-2 text-sm">
    {event?.startDate && <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${chipCls}`}><Icon name="calendar" className="h-4 w-4" />{fmtEventDate(event.startDate)}</span>}
    {event?.timeText && <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${chipCls}`}><Icon name="clock" className="h-4 w-4" />{event.timeText}</span>}
    {event?.venue && <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${chipCls}`}><Icon name="map-pin" className="h-4 w-4" />{event.venue}</span>}
  </div>
);

/**
 * Event header. With a banner (the ticket template's banner, else the event's image) the banner
 * fills the header, uncovered, with the event details on a strip below it. Without one (or if the
 * image can't be loaded) a designed header in the template's main colour: date tile, title, a
 * light dot pattern and ticket-style notches.
 */
export const EventHero = ({ event, template, compact }) => {
  const [failed, setFailed] = useState([]);
  const img = [template?.bannerImage, event?.imageUrl].filter(Boolean).map(resolveMediaUrl).find((u) => !failed.includes(u));
  const color = template?.colors?.primary || '#6d28d9';
  const titleCls = `font-bold leading-tight tracking-tight ${compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-4xl'}`;

  if (img) {
    return (
      <section className="overflow-hidden rounded-3xl bg-white shadow-xl shadow-gray-200/70 ring-1 ring-gray-100">
        <div className={`relative w-full bg-gray-100 ${compact ? 'aspect-[5/1]' : 'aspect-[16/7] sm:aspect-[3/1]'}`}>
          <img src={img} alt={event?.title || ''} onError={() => setFailed((f) => [...f, img])} className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="flex items-center gap-4 border-t-4 px-5 py-4 sm:gap-5 sm:px-7 sm:py-5" style={{ borderColor: color }}>
          <DateTile date={event?.startDate} color={color} small={compact} />
          <div className="min-w-0">
            <h1 className={`${titleCls} text-gray-900`}>{event?.title}</h1>
            <EventFacts event={event} chipCls="bg-gray-100 text-gray-700" />
          </div>
        </div>
      </section>
    );
  }

  const light = isLight(color);
  const ink = light ? 'text-gray-900' : 'text-white';
  const soft = light ? 'bg-black/5' : 'bg-white/15';
  return (
    <section className={`relative overflow-hidden rounded-3xl shadow-xl shadow-gray-300/50 ${ink}`} style={{ background: color }}>
      <svg className={`absolute inset-0 h-full w-full ${light ? 'opacity-[0.08]' : 'opacity-[0.14]'}`} aria-hidden="true">
        <defs>
          <pattern id="event-hero-dots" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.6" fill={light ? '#000' : '#fff'} />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#event-hero-dots)" />
      </svg>
      <span aria-hidden="true" className={`absolute -right-20 -top-24 h-72 w-72 rounded-full border-[32px] ${light ? 'border-black/5' : 'border-white/10'}`} />
      <span aria-hidden="true" className={`absolute -bottom-24 right-28 hidden h-52 w-52 rounded-full border-[20px] sm:block ${light ? 'border-black/5' : 'border-white/10'}`} />
      <span aria-hidden="true" className="absolute -left-4 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-slate-50" />
      <span aria-hidden="true" className="absolute -right-4 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-slate-50" />

      <div className={`relative flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-7 ${compact ? 'px-6 py-6 sm:px-9' : 'px-7 py-9 sm:px-12 sm:py-14'}`}>
        <DateTile date={event?.startDate} color={light ? '#111827' : color} small={compact} />
        <div className="min-w-0">
          <p className={`mb-2 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[0.6875rem] font-semibold uppercase tracking-[0.18em] ${soft}`}>
            <Icon name="tag" className="h-3.5 w-3.5" />Event tickets
          </p>
          <h1 className={titleCls}>{event?.title}</h1>
          <EventFacts event={event} chipCls={soft} />
        </div>
      </div>
    </section>
  );
};

/** Full-page message (link invalid, sales closed, order not found). */
export const PublicMessage = ({ icon = 'info', title, children, tone = 'violet' }) => (
  <div className="mx-auto max-w-lg rounded-3xl border border-gray-100 bg-white p-8 text-center shadow-xl shadow-gray-200/60">
    <span className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${tone === 'rose' ? 'bg-rose-50 text-rose-600' : tone === 'amber' ? 'bg-amber-50 text-amber-600' : 'bg-violet-50 text-violet-600'}`}>
      <Icon name={icon} className="h-8 w-8" />
    </span>
    <h2 className="mt-4 text-xl font-semibold text-gray-900">{title}</h2>
    <div className="mt-2 text-sm leading-relaxed text-gray-600">{children}</div>
  </div>
);

export const Section = ({ step, title, done, children, aside }) => (
  <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-3 text-base font-semibold text-gray-900">
        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold ${done ? 'bg-emerald-500 text-white' : 'bg-violet-600 text-white'}`}>
          {done ? <Icon name="check" className="h-4 w-4" strokeWidth={3} /> : step}
        </span>
        {title}
      </h2>
      {aside}
    </div>
    {children}
  </section>
);

export const Field = ({ label, hint, error, required, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-sm font-medium text-gray-700">{label}{required && <span className="text-rose-500"> *</span>}</span>
    {children}
    {error ? <span className="mt-1 block text-xs font-medium text-rose-600">{error}</span> : hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
  </label>
);

export const inputCls = 'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[15px] shadow-sm transition focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/15 disabled:bg-gray-50 disabled:text-gray-500';
